import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HeaderTemplate } from "Components/templates/Header";
import { FooterTemplate } from "Components/templates/FooterTemplate";
import { ToastFeedback } from "Components/atoms/ToastFeedback";
import { Button } from "Components/atoms/Button";
import { AlertForm } from "Components/organisms/AlertForm";
import { AlertsInfoSections } from "pages/AlertsV2/AlertsInfoSections";
import { AlertFormHero } from "pages/AlertsV2/AlertFormHero";
import { BelowStage } from "pages/AlertsV2/marketing.styles";
import { useAuth } from "shared/contexts/AuthContext";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";
import {
  getAlert,
  editAlert,
  AlertsApiError,
} from "shared/services/alerts/alerts.service";
import { startLogin } from "shared/auth/auth";
import {
  airportsToBody,
  centsToBRL,
  channelsFromFlags,
  channelsToFlags,
  formatAirportCityLabel,
  formatBRL,
  formatDateRangeShort,
} from "shared/utils/alerts/alertFormat";
import { getAirports } from "shared/services/contexts/airport.service";
import { GlobalStyles } from "styles/globalStyles";
import { theme } from "styles/theme";
import { ThemeProvider } from "styled-components";
import { Page, HeaderArea, Content, StateMessage, StateTitle } from "./styles";
import { AccountLoader } from "pages/Account/AccountLoader";
import type {
  Alert,
  EditAlertPayload,
} from "shared/services/alerts/alerts.types";
import type { AlertFormValues } from "Components/organisms/AlertForm/types";
import type { Airport } from "shared/types/airportsTypes";

type LoadState = "loading" | "not_found" | "generic_error" | "loaded";

/**
 * Alertas criados no app podem ter vários aeroportos; se a seleção do formulário
 * não mudou, mantém a lista original em vez de reduzi-la ao primeiro código.
 */
const resolveAirports = (selected: Airport | null, original: string[]) =>
  selected && selected.code !== original[0] ? [selected.code] : original;

/**
 * Página de edição de alertas de passagens (`/alertas/:id/editar`).
 *
 * Segue o layout da tela de cadastro (promessa à esquerda, formulário em
 * card à direita), com texto e botão de edição. Carrega o alerta por ID e
 * permite alterar origem, destino, datas, preço e canais.
 */
export default function EditAlertPage() {
  const { id } = useParams<{ id: string }>();
  const { user, refreshSession } = useAuth();
  const userId = user?.id;
  const navigate = useNavigate();

  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [alert, setAlert] = useState<Alert | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  const airports = useMemo(() => getAirports(), []);

  useEffect(() => {
    const loadAlert = async () => {
      if (!id) {
        setLoadState("not_found");
        return;
      }

      try {
        const fetchedAlert = await getAlert(id);

        // Verify ownership
        if (fetchedAlert.userID !== userId) {
          setLoadState("not_found");
          return;
        }

        setAlert(fetchedAlert);
        setLoadState("loaded");
      } catch (error) {
        if (error instanceof AlertsApiError && error.status === 401) {
          const u = await refreshSession();
          if (!u) {
            startLogin({ source: "alerts", replaceCurrentPage: true });
            return;
          }
          setLoadState("generic_error");
        } else if (error instanceof AlertsApiError && error.status === 404) {
          setLoadState("not_found");
        } else {
          setLoadState("generic_error");
        }
      }
    };

    if (userId) {
      loadAlert();
    }
  }, [id, userId, refreshSession]);

  const initialValues: AlertFormValues | null = useMemo(() => {
    if (!alert) return null;

    const origin = airports.findAirportByCode(alert.originAirports[0]);
    const destination = airports.findAirportByCode(
      alert.destinationAirports[0],
    );

    return {
      origin: origin || {
        code: alert.originAirports[0],
        name: alert.originAirports[0],
        description: alert.originAirports[0],
      },
      destination: destination || {
        code: alert.destinationAirports[0],
        name: alert.destinationAirports[0],
        description: alert.destinationAirports[0],
      },
      openDates: alert.matchType === "onlyPrice",
      departureDates: alert.departureDate,
      returnDates: alert.returnDate,
      priceCents: Math.round(alert.maxPrice * 100),
      channels: channelsFromFlags(alert),
    };
  }, [alert, airports]);

  const handleSubmit = async (values: AlertFormValues) => {
    if (!user || !alert) return;
    setSaving(true);

    try {
      const payload: EditAlertPayload = {
        id: alert.id,
        userID: user.id,
        originAirports: airportsToBody(
          resolveAirports(values.origin, alert.originAirports),
        ),
        destinationAirports: airportsToBody(
          resolveAirports(values.destination, alert.destinationAirports),
        ),
        maxPrice: centsToBRL(values.priceCents),
        departureDate: values.openDates ? [] : values.departureDates,
        returnDate: values.openDates ? [] : values.returnDates,
        numberOfAdults: 1,
        numberOfChildren: 0,
        numberOfBabies: 0,
        maxPriceMiles: 300,
        withLuggage: false,
        isStopped: alert.isStopped,
        ...channelsToFlags(values.channels),
        matchType: values.openDates ? "onlyPrice" : "dateRange",
      };

      await editAlert(payload);

      sendGTMEvent("alertManage", {
        platform: getPlatformType(),
        action: "edit",
        result: "success",
        alertId: alert.id,
      });

      navigate("/alertas", {
        state: {
          feedback: {
            type: "success",
            message: "Alerta atualizado. Suas alterações foram salvas.",
          },
        },
      });
    } catch (error) {
      if (error instanceof AlertsApiError && error.status === 401) {
        const u = await refreshSession();
        if (!u) {
          startLogin({ source: "alerts", replaceCurrentPage: true });
          return;
        }
        setFeedback({
          message: "Não foi possível salvar. Tente novamente em instantes.",
          type: "error",
        });
      } else if (error instanceof AlertsApiError) {
        setFeedback({
          message: `Não foi possível salvar. ${error.message}`,
          type: "error",
        });
        sendGTMEvent("alertManage", {
          platform: getPlatformType(),
          action: "edit",
          result: "error",
          alertId: alert.id,
        });
      } else {
        setFeedback({
          message: "Não foi possível salvar. Tente novamente em instantes.",
          type: "error",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadState === "loading") {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Page>
          <HeaderArea>
            <HeaderTemplate />
          </HeaderArea>
          <Content>
            <AccountLoader label="Carregando alerta..." fullPage />
          </Content>
          <FooterTemplate />
        </Page>
      </ThemeProvider>
    );
  }

  if (loadState === "not_found") {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Page>
          <HeaderArea>
            <HeaderTemplate />
          </HeaderArea>
          <Content>
            <StateMessage>
              <StateTitle>Alerta não encontrado.</StateTitle>
              <Button
                label="Voltar para meus alertas"
                backgroundColor="mediumBlue"
                onClick={() => navigate("/alertas")}
                primary
              />
            </StateMessage>
          </Content>
          <FooterTemplate />
        </Page>
      </ThemeProvider>
    );
  }

  if (loadState === "generic_error") {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Page>
          <HeaderArea>
            <HeaderTemplate />
          </HeaderArea>
          <Content>
            <StateMessage>
              <StateTitle>Não foi possível carregar o alerta.</StateTitle>
              <Button
                label="Tentar novamente"
                backgroundColor="mediumBlue"
                onClick={() => window.location.reload()}
                primary
              />
            </StateMessage>
          </Content>
          <FooterTemplate />
        </Page>
      </ThemeProvider>
    );
  }

  if (!alert || !initialValues) {
    return null;
  }

  const originCode = alert.originAirports[0];
  const destinationCode = alert.destinationAirports[0];

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Page>
        <HeaderArea>
          <HeaderTemplate />
        </HeaderArea>

        <AlertFormHero
          mode="edit"
          backTo="/alertas"
          alert={{
            originCode,
            originLabel: formatAirportCityLabel(originCode),
            destinationCode,
            destinationLabel: formatAirportCityLabel(destinationCode),
            departureNote: initialValues.openDates
              ? "Ida: qualquer data"
              : `Ida: ${formatDateRangeShort(initialValues.departureDates)}`,
            returnNote: initialValues.openDates
              ? "Volta: qualquer data"
              : initialValues.returnDates.length > 0
                ? `Volta: ${formatDateRangeShort(initialValues.returnDates)}`
                : "Sem volta",
            price: formatBRL(alert.maxPrice),
          }}
        >
          <AlertForm
            variant="card"
            mode="edit"
            initialValues={initialValues}
            saving={saving}
            userEmail={user?.email || ""}
            onSubmit={handleSubmit}
            submitLabel="Salvar alterações"
            title="Editar alerta de preço"
            footnote="As alterações valem a partir de agora."
            status={alert.isStopped ? "paused" : "active"}
          />
        </AlertFormHero>

        <ToastFeedback
          message={feedback?.message ?? ""}
          type={feedback?.type ?? "success"}
          isOpen={Boolean(feedback)}
          onClose={() => setFeedback(null)}
        />
        <BelowStage>
          <AlertsInfoSections />
        </BelowStage>
        <FooterTemplate />
      </Page>
    </ThemeProvider>
  );
}
